// Core
import React, {
  useMemo,
  useState,
} from 'react';

// Libraries
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Plugins
import { sortItems } from '../../../plugins/general';

// Components
import { Button, ButtonType } from '../Button';
import { Col } from '../Col';
import {
  Paragraph,
  ParagraphAlign,
  ParagraphSize,
  ParagraphWeight,
} from '../Paragraph';
import { Row } from '../Row';
import {
  CaretDown,
  Inbox,
  SortAscend,
  SortDescend,
} from '../../icons';

// Component
import {
  CardButtonContainer,
  CardContainer,
  CardContent,
  CardContentInner,
  CardContentWrapper,
  CardDetails,
  CardHeader,
  CardImageContainer,
  CardTitleContainer,
  CaretDownContainer,
  Container,
  EmptyState,
  SortContainer,
  SortIcon,
  SortIconsContainer,
} from './CardList.style';
import {
  CardListProps,
  CardRow,
  CardSubItem,
} from './CardList.types';

// Types
import { Theme } from '../../../types/theme';
import { SortOrder } from '../../../types/global';

/* ***********************************************************************************************
***************************************** CARD COMPONENT *****************************************
*********************************************************************************************** */

interface CardProps {
  image?: string | JSX.Element;
  title: string;
  subtitle?: string;
  subItems: CardSubItem[];
  action?: {
    label: string;
    onClick: () => void;
  };
  disabled?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  testId: string;
}

function Card({
  image,
  title,
  subtitle,
  subItems,
  action,
  disabled,
  isExpanded,
  onToggle,
  testId,
}: CardProps): JSX.Element {
  const theme: Theme = useTheme();

  return (
    <CardContainer data-testid={`${testId}-card`}>
      <CardHeader data-testid={`${testId}-card-header`} onClick={onToggle}>
        {image && (
          <CardImageContainer>
            {
              typeof image === 'string'
                ? <img src={image} alt="CardListImage" />
                : image
            }
          </CardImageContainer>
        )}
        <CardTitleContainer>
          <Paragraph
            size={ParagraphSize.sm}
            color={theme.color.cardList.titleTextColor}
            weight={ParagraphWeight.bold}
            preventOverflow
          >
            {title}
          </Paragraph>
          {subtitle && (
            <Paragraph weight={ParagraphWeight.medium} preventOverflow>
              {subtitle}
            </Paragraph>
          )}
        </CardTitleContainer>
        <CaretDownContainer isExpanded={isExpanded}>
          <CaretDown />
        </CaretDownContainer>
      </CardHeader>
      <CardContentWrapper isExpanded={isExpanded}>
        <CardContentInner>
          <CardContent>
            <CardDetails>
              {subItems.map((subItem: CardSubItem) => (
                <Row key={subItem.id ?? subItem.label}>
                  <Col cols={12}>
                    <Paragraph
                      size={ParagraphSize.sm}
                      color={theme.color.cardList.detailsLabelColor}
                      preventOverflow
                    >
                      {subItem.label}
                    </Paragraph>
                  </Col>
                  <Col cols={12}>
                    <Paragraph
                      size={ParagraphSize.sm}
                      weight={ParagraphWeight.medium}
                      align={ParagraphAlign.right}
                      preventOverflow
                    >
                      {subItem.value}
                    </Paragraph>
                  </Col>
                </Row>
              ))}
            </CardDetails>
            {action && (
              <CardButtonContainer>
                <Button
                  testId={testId}
                  type={ButtonType.secondary}
                  onClick={action.onClick}
                  disabled={disabled}
                >
                  {action.label}
                </Button>
              </CardButtonContainer>
            )}
          </CardContent>
        </CardContentInner>
      </CardContentWrapper>
    </CardContainer>
  );
}

/* ***********************************************************************************************
*************************************** CARD LIST COMPONENT **************************************
*********************************************************************************************** */

function CardList({
  className,
  rows,
  sortColumn,
  testId,
}: CardListProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  /* ***********************************************************************************************
  **************************************** LOCAL STATES ********************************************
  *********************************************************************************************** */

  interface State {
    expandedCardId: string | null;
    sortOrder: SortOrder;
  }

  const initialState: State = {
    expandedCardId: null,
    sortOrder: SortOrder.ascend,
  };
  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  // Sort rows by sortColumn key
  const sortedRows: CardRow[] = useMemo(
    () => sortItems(rows, sortColumn.key, state.sortOrder),
    [rows, state.sortOrder, sortColumn.key],
  );

  const handleSortToggle = (): void => {
    setState((prev: State): State => ({
      ...prev,
      sortOrder: prev.sortOrder === SortOrder.ascend
        ? SortOrder.descend
        : SortOrder.ascend,
    }));
  };

  return (
    <Container className={className} data-testid={testId}>
      {/* Sorting Header */}
      <SortContainer onClick={handleSortToggle}>
        <Paragraph
          size={ParagraphSize.sm}
          weight={ParagraphWeight.bold}
          color={theme.color.cardList.sortTextColor}
        >
          {sortColumn.label}
        </Paragraph>
        <SortIconsContainer>
          <SortIcon isActive={state.sortOrder === SortOrder.ascend}>
            <SortAscend />
          </SortIcon>
          <SortIcon isActive={state.sortOrder === SortOrder.descend}>
            <SortDescend />
          </SortIcon>
        </SortIconsContainer>
      </SortContainer>

      {/* List of Cards */}
      {sortedRows.length > 0 ? (
        sortedRows.map((row: CardRow, index: number) => (
          <Card
            key={row.key}
            image={row.image}
            title={row.title}
            subtitle={row.subtitle}
            subItems={row.subItems}
            action={row.action}
            disabled={row.disabled}
            isExpanded={state.expandedCardId === row.key}
            onToggle={() => {
              setState((prev: State): State => ({
                ...prev,
                expandedCardId: prev.expandedCardId === row.key ? null : row.key,
              }));
            }}
            testId={`card-action-button-${index}`}
          />
        ))
      ) : (
        <EmptyState>
          <Inbox />
          {t('common.noData')}
        </EmptyState>
      )}
    </Container>
  );
}

export { CardList };
